import {
  blankJobConfig,
  type ConfigCriterion,
  type EmploymentType,
  type IcpItemRequest,
  type JobConfig,
  type RoleTemplateRequest,
  type RoleTemplateView,
  type WorkMode,
} from '@nest-recruit/shared';
import { itemInputOf, itemOfInput } from '../../../icp/icp-form';
import { namedCriteria, type CriterionRow } from '../builder/CriteriaTable';
import { namedLines, type MatrixRow } from '../builder/MatrixTable';
import type { QuestionDraft } from '../FormBuilder';

// ADMIN-05 (Role templates A): the editor's draft, what Save sends, the list's search and lines, and "What a recruiter
// gets". Pure, so each is tested alone.

export interface TemplateDraft {
  readonly name: string;
  readonly title: string;
  readonly roleFamily: string;
  readonly ventureId: string | null;
  readonly departmentId: string | null;
  readonly employmentType: EmploymentType;
  readonly workMode: WorkMode;
  readonly isActive: boolean;
  readonly description: string;
  readonly responsibilities: string;
  readonly requirements: string;
  /** ADR 0062 §4: the template's ideal candidate, as lines; empty is none. The ratings are the baseline a job starts from. */
  readonly profile: readonly IcpItemRequest[];
  readonly criteria: readonly CriterionRow[];
  /** The §L1 type each criterion is scored as, kept by row so a save doesn't lose it. */
  readonly legacy: ReadonlyMap<string, ConfigCriterion['legacy']>;
  readonly matrix: readonly MatrixRow[];
  readonly brief: JobConfig['brief'];
  readonly formFields: JobConfig['formFields'];
  readonly questions: readonly QuestionDraft[];
  readonly pipeline: JobConfig['pipeline'];
  /**
   * Steps 5–7 (S12). Not edited here — a job's wizard is where they are set — but carried through, so saving this
   * template never drops a submitted job's interview plan, its test or its offer settings.
   */
  readonly assessment: JobConfig['assessment'];
  readonly interviewRounds: JobConfig['interviewRounds'];
  readonly offer: JobConfig['offer'];
  readonly testFamilyId: JobConfig['testFamilyId'];
}

export function templateDraftOf(template: RoleTemplateView | null): TemplateDraft {
  const config = template?.config ?? blankJobConfig();
  return {
    name: template?.name ?? '',
    title: template?.title ?? '',
    roleFamily: template?.roleFamily ?? '',
    ventureId: template?.ventureId ?? null,
    departmentId: template?.departmentId ?? null,
    employmentType: template?.employmentType ?? 'full_time',
    workMode: template?.workMode ?? 'onsite',
    isActive: template?.isActive ?? true,
    description: config.description ?? '',
    responsibilities: config.responsibilities ?? '',
    requirements: config.requirements ?? '',
    profile: config.profile?.items ?? [],
    // The stored copy carries no row ids; the editors key on them, so give each one.
    ...criteriaDraftOf(config.criteria),
    matrix: config.matrix.map((line) => ({ key: line.key ?? crypto.randomUUID(), label: line.label, anchors: line.anchors, isCritical: line.isCritical, usedIn: line.usedIn })),
    brief: config.brief,
    formFields: config.formFields,
    questions: config.questions.map((question) => ({ ...question, id: crypto.randomUUID() })),
    pipeline: config.pipeline,
    assessment: config.assessment,
    interviewRounds: config.interviewRounds,
    offer: config.offer,
    testFamilyId: config.testFamilyId,
  };
}

function criteriaDraftOf(criteria: readonly ConfigCriterion[]): Pick<TemplateDraft, 'criteria' | 'legacy'> {
  const rows = criteria.map((criterion) => ({ ...criterion, id: crypto.randomUUID() }));
  return {
    criteria: rows.map(({ legacy: _legacy, ...row }) => row),
    legacy: new Map(rows.map((row) => [row.id, row.legacy])),
  };
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** What Save sends. The editor's row ids never leave it: a template stores the setup, never rows. */
export function templateBodyOf(draft: TemplateDraft): RoleTemplateRequest {
  return {
    name: draft.name.trim(),
    title: draft.title.trim(),
    roleFamily: text(draft.roleFamily),
    ventureId: draft.ventureId,
    departmentId: draft.departmentId,
    employmentType: draft.employmentType,
    workMode: draft.workMode,
    isActive: draft.isActive,
    config: {
      description: text(draft.description),
      responsibilities: text(draft.responsibilities),
      requirements: text(draft.requirements),
      profile: draft.profile.length > 0 ? { items: draft.profile.map((item) => itemInputOf(itemOfInput(item))) } : null,
      criteria: namedCriteria(draft.criteria).map(({ id, ...criterion }) => ({ ...criterion, lookFor: [...criterion.lookFor], partlyCounts: [...criterion.partlyCounts], legacy: draft.legacy.get(id) ?? null })),
      matrix: namedLines(draft.matrix).map((line) => ({ ...line, usedIn: [...line.usedIn] })),
      brief: draft.brief,
      formFields: draft.formFields,
      questions: draft.questions.map(({ id: _id, ...question }) => question),
      pipeline: draft.pipeline,
      assessment: draft.assessment,
      interviewRounds: draft.interviewRounds,
      offer: draft.offer,
      testFamilyId: draft.testFamilyId,
    },
  };
}

/** The same template as Save would send it, whatever order any object's keys are in. */
export function isSameTemplateDraft(left: TemplateDraft, right: TemplateDraft): boolean {
  return canonicalJson(templateBodyOf(left)) === canonicalJson(templateBodyOf(right));
}

/** The parts the editor edits are the same: a new version that only certified or decided it leaves a draft alone. */
export function isSameEditableTemplate(left: RoleTemplateView, right: RoleTemplateView): boolean {
  const editable = (view: RoleTemplateView): unknown => ({
    name: view.name,
    title: view.title,
    roleFamily: view.roleFamily,
    ventureId: view.ventureId,
    departmentId: view.departmentId,
    employmentType: view.employmentType,
    workMode: view.workMode,
    isActive: view.isActive,
    config: view.config,
  });
  return canonicalJson(editable(left)) === canonicalJson(editable(right));
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, inner: unknown) =>
    typeof inner === 'object' && inner !== null && !Array.isArray(inner)
      ? Object.fromEntries(Object.entries(inner).sort(([a], [b]) => a.localeCompare(b)))
      : inner,
  );
}

// --- The list ---------------------------------------------------------------------------------------------------

/** "Finance · Nest Textiles · Accounts": the family and where it applies. */
export function templatePlaceOf(template: Pick<RoleTemplateView, 'roleFamily' | 'ventureName' | 'departmentName'>): string {
  const place = template.departmentName ? [template.ventureName, template.departmentName] : [template.ventureName ?? 'Every department'];
  return [template.roleFamily, ...place].filter((part): part is string => typeof part === 'string' && part !== '').join(' · ');
}

export function usedText(count: number): string {
  return `used ${count} ${count === 1 ? 'time' : 'times'}`;
}

/** Name, title, family, venture or department, ignoring capitals. */
export function matchesTemplate(template: RoleTemplateView, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return [template.name, template.title, template.roleFamily, template.ventureName, template.departmentName, template.submittedByName].some(
    (part) => typeof part === 'string' && part.toLowerCase().includes(needle),
  );
}

/** Waiting first (the oldest at the top, as a queue), then the library, certified first as recruiters see them (F5.23). */
export function groupedTemplates(templates: readonly RoleTemplateView[]): { readonly waiting: readonly RoleTemplateView[]; readonly library: readonly RoleTemplateView[] } {
  return {
    waiting: templates.filter((template) => template.status === 'draft').sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)),
    library: templates
      .filter((template) => template.status !== 'draft')
      .sort((a, b) => Number(b.isCertified) - Number(a.isCertified) || a.name.localeCompare(b.name)),
  };
}

// --- What a recruiter gets ---------------------------------------------------------------------------------------

/** Whether recruiters are offered it, in a sentence. Only an approved template that is on is offered. */
export function offeredText(template: Pick<RoleTemplateView, 'status'> | null, isActive: boolean): string {
  if (template?.status === 'draft') return 'Waiting for approval, so recruiters can’t pick it yet.';
  if (template?.status === 'rejected') return 'Rejected, so recruiters can’t pick it.';
  if (!isActive) return 'Off, so recruiters don’t see it. It is kept.';
  return 'Recruiters can pick it in “Start from template”.';
}

export interface FilledLine {
  readonly label: string;
  readonly detail: string;
  readonly isFilled: boolean;
}

function count(amount: number, one: string, many: string): string {
  return `${amount} ${amount === 1 ? one : many}`;
}

/** "Filled in for them": each part of the job the template fills, as it stands in the editor now. */
export function filledLines(draft: TemplateDraft, modules: { readonly isCriteriaOn: boolean; readonly isFormOn: boolean; readonly stageCount: number }): readonly FilledLine[] {
  const hasDescription = [draft.description, draft.responsibilities, draft.requirements].some((value) => value.trim() !== '');
  const criteria = namedCriteria(draft.criteria).length;
  const lines = namedLines(draft.matrix).length;
  const rounds = draft.interviewRounds.length;
  return [
    { label: 'Job description', detail: hasDescription ? 'Filled in' : 'Empty', isFilled: hasDescription },
    { label: 'Ideal candidate', detail: count(draft.profile.length, 'line', 'lines'), isFilled: draft.profile.length > 0 },
    ...(modules.isCriteriaOn ? [{ label: 'Screening criteria', detail: count(criteria, 'criterion', 'criteria'), isFilled: criteria > 0 }] : []),
    { label: 'Scoring matrix', detail: `${count(lines, 'line', 'lines')}, ${count(rounds, 'round', 'rounds')}`, isFilled: lines > 0 },
    ...(modules.isFormOn ? [{ label: 'Application form', detail: count(draft.questions.length, 'question', 'questions'), isFilled: true }] : []),
    { label: 'Pipeline', detail: count(modules.stageCount, 'stage', 'stages'), isFilled: true },
  ];
}
